<template>
  <div>
    <vx-card class="mb-5">
      <heading size="h2" style-size="h4" class="mb-5">A Day in the Life of a Psychologist</heading>
      <p class="mb-5">
        Watch the video below to gain some insight into a day in the life of a clinical psychologist
        who works with students at an American university. Even though some of the information is
        US-focused, it does give an interesting perspective on some of the daily tasks and routines
        of a working psychologist.
      </p>
      <p class="mb-5">
        The video is only around 6 minutes long, but you can use the timestamps below to jump to an
        area of interest if you’re in a hurry!
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5" role="list">
          <p role="listitem" class="mb-2">
            <u>0:27</u> How did you know that being a Clinical Psychologist is what you wanted to
            do?
          </p>
          <p role="listitem" class="mb-2"><u>0:57</u> What is a typical day and week for you?</p>
          <p role="listitem" class="mb-2">
            <u>2:14</u> What do you like and not like about your job? What are the pros and cons of
            this type of work?
          </p>
          <p role="listitem" class="mb-2"><u>3:01</u> Settings to apply psychology degree</p>
          <p role="listitem" class="mb-2"><u>4:40</u> Resources for students</p>
          <p role="listitem" class="mb-2">
            <u>5:39</u> Path and steps to becoming a Clinical Psychologist
          </p>
          <p role="listitem" class="mb-2"><u>7:38</u> Other things to know about this career</p>
          <p role="listitem" class="mb-2"><u>8:22</u> Self Care Tips</p>
          <p role="listitem" class="mb-2"><u>8:48</u> Personal Quote</p>
          <p role="listitem" class="mb-2"><u>9:11</u> General advice for young adults</p>
        </div>
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
          <custom-video
            aria-label="Watch A Day in the Life of a Psychologist video"
            elem-id="video-btn-0"
            :video-image="intro_videos[1].image"
            :video-url="intro_videos[1].video"
          />
        </div>
      </div>
    </vx-card>

    <vx-card>
      <p class="mb-5">
        The world of psychology is broad and diverse. There are many fields a psychologist can work
        in.
      </p>
      <p class="mb-5">
        The psychologists featured below come from a variety of settings across Australia and as
        students many of them took time to discover their area of interest.
      </p>
      <p class="mb-5">
        Read through the below collection of psychologists career stories and be encouraged by your
        future possibilities.
      </p>
      <ul class="plain-list">
        <li class="mb-5">
          <strong>Tracey Wade</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Academic"
            target="_blank"
            >academic psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Antonio Paolini</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Biological"
            target="_blank"
            >biological psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Rita Princi</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Child-adolescent-and-family"
            target="_blank"
            >child, adolescent and family psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Cindy Wall</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Clinical"
            target="_blank"
            >clinical psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Wendy Longley</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Clinical-neuro"
            target="_blank"
            >clinical neuro psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Frances Martin</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Cognitive"
            target="_blank"
            >cognitive psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Meg Smith</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Community"
            target="_blank"
            >community psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Nicole Detering</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Counselling"
            target="_blank"
            >counselling psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Darren Stops</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Educational-and-developmental"
            target="_blank"
            >educational and developmental psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Rob Hall</strong> - working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Environmental"
            target="_blank"
            >environmental psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>David Whittingham</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Forensic"
            target="_blank"
            >forensic psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Evelyn Howe</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Health"
            target="_blank"
            >health psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Peter Renshaw</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Human-factors"
            target="_blank"
            >human factors psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Graham Chant</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Market-research"
            target="_blank"
            >market research psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Glen Menezes</strong> - Working as a
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Military"
            target="_blank"
            >psychologist in the military</a
          >
        </li>
        <li class="mb-5">
          <strong>Joanne Fitzgerald</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Research"
            target="_blank"
            >organisational psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Jason Mattingley</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Research"
            target="_blank"
            >research psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Melisah Feeney</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Social"
            target="_blank"
            >social psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Ruth Anderson</strong> - Working in
          <a
            href="https://www.psychology.org.au/Training-and-careers/Careers-and-studying-psychology/careers-in-psychology/Psychologists-talk-about-careers/Sport"
            target="_blank"
            >sports psychology</a
          >
        </li>
        <li class="mb-5">
          <strong>Philippa Weiland</strong> -
          <a
            href="https://www.bing.com/videos/search?q=psychology+Australia&&view=detail&mid=B3823D943D1875962C32B3823D943D1875962C32&&FORM=VRDGAR&ru=%2Fvideos%2Fsearch%3Fq%3Dpsychology%2520Australia%26qs%3Dn%26sp%3D-1%26ghc%3D1%26pq%3Dpsychology%2520australia%26sc%3D8-20%26sk%3D%26cvid%3D1A6F7EAA0282488C85AE17934E5B7F6A%26FORM%3DVDVVXX"
            target="_blank"
            >Senior psychologist, Australian Army</a
          >
        </li>
      </ul>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4">Stress</heading>
      <heading size="h3" style-size="h4" class="mb-5">What Is Stress?</heading>
      <p class="mb-5">
        Stress and stress-related conditions play a large role in many of the disorders that
        psychologists deal with daily.
      </p>
      <p class="mb-5">
        Because of this, helping clients to manage stress and the symptoms of stress is an important
        aspect of being a psychologist. Knowing how to treat stress is also important for being a
        psychologist as the job itself can be stressful at times.
      </p>
      <p class="mb-5">
        While stress is generally considered to be a ‘bad’ thing, some stress can be good for us. It
        can motivate us to finish the things we need to finish and it can help drive us on to
        perform well in difficult situations.
      </p>
      <p class="mb-5">
        However, when stress continues over an extended period it can have a really negative impact
        on a person’s physical and mental health.
      </p>

      <heading size="h3" style-size="h4" class="mb-5">What are the main types of stress?</heading>
      <p class="mb-5">There are two main types of stress: acute and chronic.</p>
      <heading size="h3" style-size="h4" class="mb-5">Acute Stress</heading>
      <img
        src="@/assets/images/experiences/psychology/stress_1.jpg"
        alt=""
        role="presentation"
        class="my-2 mb-2 float-right"
      />

      <p class="mb-5">
        This type of stress lasts for a short time. It is a normal part of our everyday life. We can
        experience acute stress in situations such as moving house, doing an exam, having a baby, or
        starting a new job.
      </p>
      <p class="mb-5">
        Usually, once the situation has passed, the body and mind bounce back as things return to
        normal. This stress can even help us build resilience, enabling us to take the minor
        stresses of life in our stride in the future.
      </p>
      <p class="mb-5">
        Sometimes though, when acute stress is particularly severe, it can lead to significant
        mental health problems. For example, a violent assault may, for some individuals, lead to
        problems such as post-traumatic stress disorder, also known as PTSD.
      </p>

      <heading size="h3" style-size="h4" class="mb-5">Chronic Stress</heading>
      <p class="mb-5">
        Chronic stress, unlike acute stress, is the stress that lasts for a long period. Chronic
        stress can happen in drawn-out situations and circumstances such as:
      </p>
      <ul class="default-list" role="list">
        <li role="listitem">Taking care of someone with high needs for an extended time</li>
        <li role="listitem">Working too hard for too long</li>
        <li role="listitem">Experiencing ongoing financial problems</li>
        <li role="listitem">Being victimised by a school or workplace bully</li>
        <li role="listitem">Living in a warzone.</li>
      </ul>
      <p class="mb-5">
        In these types of circumstances, often an individual doesn’t get a chance to recover. Also,
        stress can be cumulative. So, if several stressful events happen one after another, or at
        the same time, stress levels can go up rapidly and stay high. This can lead to many
        unpleasant and dangerous symptoms.
      </p>

      <heading size="h3" style-size="h4" class="mb-5">The Symptoms of Stress</heading>
      <p class="mb-5">
        Stress can cause many changes to the body and the mind. We call these changes ‘symptoms’.
        Symptoms are signs of physical or mental health problems.
      </p>
      <p class="mb-5">
        Stressful events cause our body to activate the nervous system and release chemical
        messengers called hormones. These hormones, such as cortisol and adrenalin, can lead to a
        faster heart rate, increased blood pressure, and more tension in our muscles. Our breathing
        speeds up too and we may begin to sweat more.
      </p>
      <p class="mb-5">
        These physical changes can be indicators of acute stress and they can actually help us to
        react better in stressful situations. It’s when the stress levels stay high that the
        individual can experience more symptoms.
      </p>
      <p class="mb-5">
        Check out the table below for some of the many physical symptoms of stress.
      </p>
      <table class="table default-table" aria-label="physical symptoms of stress">
        <thead>
          <tr>
            <th colspan="2" scope="col">Symptoms of Stress</th>
          </tr>
          <tr>
            <th scope="col">Physical</th>
            <th scope="col">Mental</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <ul>
                <li>Increased heart rate/palpitations</li>
                <li>High blood pressure</li>
                <li>Fatigue</li>
                <li>Disturbed sleep</li>
                <li>Insomnia</li>
                <li>Upset stomach</li>
                <li>Diarrhoea</li>
                <li>Frequent headaches</li>
                <li>Muscular aches and pains</li>
                <li>Weakened immune system</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Anxiety</li>
                <li>Feelings of helplessness</li>
                <li>Tearfulness</li>
                <li>Worry</li>
                <li>Fear</li>
                <li>Irritability</li>
                <li>Anger</li>
                <li>Poor memory</li>
                <li>Poor concentration</li>
                <li>Feeling overwhelmed</li>
              </ul>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="mb-5">
        Here is a short video explaining what happens in the body (physiological changes) when we
        experience stress. It’s a little complicated, but watch it a few times and it will be easier
        to understand. Remember - don’t get too <strong>stressed</strong> by the big words!
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch Symptoms of Stress video"
            elem-id="video-btn-1"
            :video-image="intro_videos[2].image"
            :video-url="intro_videos[2].video"
          />
        </div>
      </div>

      <heading size="h3" style-size="h4" class="mb-5">How to Diagnose Stress</heading>
      <div class="vx-row flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full">
          <p class="mb-5">
            Psychologists understand that people can react very differently to the same stressors.
            One classic tool to help measure the stress levels of individuals is the Perceived
            Stress Scale (PSS). This tool asks us about our thoughts and feelings about different
            situations over the last month.
          </p>
          <p class="mb-5">
            Some of the questions may seem very similar, but there will be a slightly different
            focus, so each question should be answered. The person taking the test mustn’t overthink
            things and should answer the questions fairly quickly. The questions ask how often you
            felt a certain way. Rather than try to calculate an exact number, the test taker should
            select the answer that seems to be the best fit.
          </p>
          <p class="mb-5">
            You will have an opportunity to explore this in one of your activities in the next
            section.
          </p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full">
          <img
            src="@/assets/images/experiences/psychology/lr1.png"
            alt=""
            role="presentation"
            class="mx-auto img-responsive"
          />
        </div>
      </div>

      <heading size="h3" style-size="h4" class="mb-5">Treatment</heading>
      <p class="mb-5">
        We all know that we need to exercise, eat well, sleep and be positive to help combat stress,
        however, below are some treatments used by psychologists to assist people further.
      </p>

      <p class="mb-5">
        According to the Australian Psychological Society, there are several effective ways to help
        people to reduce the stress they experience. These include:
      </p>
      <ul class="default-list" role="list">
        <li role="listitem">Cognitive Behavioural Stress Management</li>
        <li role="listitem">Stress Inoculation Training</li>
        <li role="listitem">Mindfulness-Based Stress Reduction</li>
        <li role="listitem">Relaxation-Based Approaches</li>
      </ul>

      <heading size="h3" style-size="h4" class="mb-5"
        >Cognitive Behavioural Stress Management (CBSM)
      </heading>
      <p class="mb-5">
        CBSM tries to address stress through a range of different techniques such as communication
        skills training, problem-solving skills, time management skills, thinking strategies, and
        relaxation techniques.
      </p>
      <heading size="h3" style-size="h4" class="mb-5">Stress Inoculation Training (SIT)</heading>
      <p class="mb-5">
        SIT involves teaching people skills to help manage their stress more effectively, for
        example, coping strategies and self-talk awareness. SIT helps a person see the difference
        between things they have control of and things that are beyond their control. This allows
        the person to focus on areas where they can take constructive action. SIT teaches a variety
        of coping skills that are to be practised while rehearsing stressful situations.
      </p>
      <heading size="h3" style-size="h4" class="mb-5">Mindfulness-based Stress Reduction</heading>
      <p class="mb-5">
        In recent years, mindfulness techniques have been shown to have positive stress reduction
        benefits. Mindfulness directs the person’s attention to the present moment thereby freeing
        them from the stress of worries both real and imagined. Meditation is an example of one
        commonly used mindfulness tool.
      </p>
      <heading size="h3" style-size="h4" class="mb-5">Relaxation-Based Approaches</heading>
      <p class="mb-5">
        Several relaxation-based approaches have been found to help stress reduction, especially
        when they’re practised regularly. Relaxation skills practice is usually performed in a quiet
        environment that is free from distractions. The person usually sits or lies down in a
        comfortable and relaxed position. Techniques involve teaching the body to respond to verbal
        cues and practising calming breathing exercises.
      </p>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5">Conditioning</heading>
      <p class="mb-5">
        Now we are going to have a brief look at <strong>Classical Conditioning</strong> and
        <strong>Operant Conditioning</strong> and how it affects behaviour.
      </p>
      <div class="vx-row flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full">
          <p class="mb-5"><strong>CLASSICAL CONDITIONING</strong></p>
          <p class="mb-5">
            Classical conditioning is a learning process that occurs when two stimuli are repeatedly
            paired.
          </p>
          <p class="mb-5">
            <strong>Neutral Stimulus</strong> = A stimulus that does not elicit (evokes or draws
            out) any particular response.
          </p>
          <p class="mb-5">
            <strong>Unconditioned stimulus (UCS)</strong> = Stimulus that elicits an automatic
            response.
          </p>
          <p class="mb-5">
            <strong>Unconditioned Response (UCR)</strong> = Automatic response to a non-neutral
            stimulus that does not need to be learned.
          </p>
          <p class="mb-5">
            <strong>Conditioned Response (CR)</strong> = Response previously associated with a
            non-neutral stimulus that is elicited by a neutral stimulus by conditioning.
          </p>
          <p class="mb-5">
            <strong>Conditioned Stimulus (CS)</strong> = Initially neutral stimulus.
          </p>
          <p class="mb-5">
            <strong>Acquisition</strong> = Learning phase during which a conditioned response is
            established.
          </p>
          <p class="mb-5">
            <strong>Extinction</strong> = Gradual reduction and eventual elimination of the
            conditional response after the conditioned stimulus is presented repeatedly without the
            unconditioned stimulus.
          </p>
        </div>
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full">
          <custom-video
            aria-label="Watch Conditioning video"
            elem-id="video-btn-2"
            :video-image="intro_videos[3].image"
            :video-url="intro_videos[3].video"
          />
        </div>
      </div>
      <div class="vx-row flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full">
          <img
            src="@/assets/images/experiences/psychology/lr2.png"
            alt="Classical conditioning in four panels. 1: a bowl of food makes the dog
              salivate. 2: a ringing bell alone produces no response. 3: the bell is rung
              while the food is presented and the dog salivates. 4: the bell alone now
              makes the dog salivate."
            class="mr-10 img-responsive mt-10"
          />
        </div>
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full">
          <ol class="numbered-list ml-10" role="list">
            <li role="listitem">The UCS (food) and UCR (Salivation) = Biology.</li>
            <li role="listitem">A neutral stimulus (bell) evokes no response.</li>
            <li role="listitem">
              The neutral stimulus (bell) is used during conditioning and is associated with the UCS
              (food) and they become associated (learned association).
            </li>
            <li role="listitem">
              The neutral stimulus gradually becomes a CS that produces a CR even if the original
              UCS is not present.
            </li>
          </ol>
        </div>
      </div>

      <heading size="h3" style-size="h4" class="my-5">OPERANT CONDITIONING</heading>
      <p class="mb-5">
        Operant Conditioning is a learning process in which behaviour is shaped by its consequences.
      </p>
      <p class="mb-5">
        In discussing operant conditioning, we use these 4 words: positive; negative; reinforcement;
        and punishment.
      </p>
      <div class="vx-row flex items-center justify-center">
        <div class="vx-col w-full">
          <table class="default-table mt-10">
            <caption class="visually-hidden">
              Reinforcement and punishment, positive and negative
            </caption>
            <tr class="header">
              <td></td>
              <th scope="col">Reinforcement</th>
              <th scope="col">Punishment</th>
            </tr>
            <tr>
              <th scope="row">Positive</th>
              <td>
                Something is <em>added</em> to <em>increase</em> the likelihood of a behaviour.
              </td>
              <td>
                Something is <em>added</em> to <em>decrease</em> the likelihood of a behaviour.
              </td>
            </tr>
            <tr>
              <th scope="row">Negative</th>
              <td>
                Something is <em>removed</em> to <em>increase</em> the likelihood of a behaviour.
              </td>
              <td>
                Something is <em>removed</em> to <em>decrease</em> the likelihood of a behaviour.
              </td>
            </tr>
          </table>
        </div>
      </div>
      <p class="my-5">
        You don’t need to memorise the entire video below, but try to understand as much of it as
        you can.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch operant conditioning video"
            elem-id="video-btn-3"
            :video-image="intro_videos[4].image"
            :video-url="intro_videos[4].video"
          />
        </div>
      </div>
      <p class="mb-5">Reinforcement and Punishment – What’s the Difference?</p>
      <div class="vx-row flex items-center justify-center mb-5">
        <div class="vx-col w-full">
          <table class="default-table mt-10">
            <caption class="visually-hidden">
              Reinforcement and punishment: procedure, effect on behaviour and a typical example
            </caption>
            <tr class="header">
              <td></td>
              <th scope="col">Procedure</th>
              <th scope="col">Effect on behaviour</th>
              <th scope="col">Typical example</th>
            </tr>
            <tr>
              <th scope="row">Positive reinforcement</th>
              <td>Presenting a desirable stimulus</td>
              <td>Increases target behaviour</td>
              <td>Giving a gold star on homework, resulting in a student studying more</td>
            </tr>
            <tr>
              <th scope="row">Negative reinforcement</th>
              <td>Removing an undesirable stimulus</td>
              <td>Increases target behaviour</td>
              <td>
                Static on a phone that subsides when you move to a different spot in your room,
                causing you to stand there more often
              </td>
            </tr>
            <tr>
              <th scope="row">Positive punishment</th>
              <td>Presenting a stimulus</td>
              <td>Decreases target behaviour</td>
              <td>Scolding by a pet owner, reducing a dog&rsquo;s habit of chewing on shoes</td>
            </tr>
            <tr>
              <th scope="row">Negative punishment</th>
              <td>Removing a stimulus</td>
              <td>Decreases target behaviour</td>
              <td>Taking away a favourite toy, stopping a child from throwing future tantrums</td>
            </tr>
          </table>
        </div>
      </div>
      <p class="mb-5">Even the boys in The Big Bang Theory are confused!</p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch operant conditioning video"
            elem-id="video-btn-4"
            :video-image="intro_videos[5].image"
            :video-url="intro_videos[5].video"
          />
        </div>
      </div>
    </vx-card>

    <vx-card title="Psychological Disorders">
      <div class="vx-row flex items-center justify-center mb-5">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full">
          <img
            src="@/assets/images/experiences/psychology/lr5.jpg"
            alt=""
            role="presentation"
            class="img-responsive"
          />
        </div>
      </div>
      <p class="mb-5">What is the DSM?</p>
      <p class="mb-5">
        The Diagnostic and Statistical Manual of Mental Disorders (DSM) is a guidebook widely used
        by mental health professionals. The most recent edition is the <strong>DSM-5</strong>. It
        was published in 2013.
      </p>
      <p class="mb-5">
        <a href="https://www.psychiatry.org/psychiatrists/practice/dsm"
          >The American Psychiatric Association (APA)</a
        >
        state that:
      </p>
      <p class="mb-5">
        The Diagnostic and Statistical Manual of Mental Disorders (DSM–5) is the product of more
        than 10 years of effort by hundreds of international experts in all aspects of mental
        health. Their dedication and hard work have yielded an authoritative volume that defines and
        classifies mental disorders in order to improve diagnoses, treatment, and research.
      </p>
    </vx-card>
  </div>
</template>

<script>
import CustomVideo from '@/components/CustomVideo';
import store from '@/store/store';

export default {
  components: { CustomVideo },
  data() {
    return {
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/psychology/day_in_the_life.jpg'),
          video: 'https://www.youtube.com/embed/Udi5-xbxc0U?autoplay=1',
        },
        2: {
          image: require('@/assets/images/experiences/psychology/stress.jpg'),
          video: 'https://www.youtube.com/embed/m0s3039GYxc',
        },
        3: {
          image: require('@/assets/images/experiences/psychology/classical_conditoning_trigger.jpg'),
          video: 'https://www.youtube.com/embed/gxEMkOtB8tM?autoplay=1',
        },
        4: {
          image: require('@/assets/images/experiences/psychology/operant_conditoning.jpg'),
          video: 'https://www.youtube.com/embed/SP6TbbgycS4',
        },
        5: {
          image: require('@/assets/images/experiences/psychology/big_bang.jpg'),
          video: 'https://www.youtube.com/embed/LhI5h5JZi-U',
        },
      },
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  methods: {
    showVideoFile(video) {
      store.dispatch('popup/showIframePopup', video);
    },
    showVideoYt(url) {
      this.ytVideoUrl = url;
      this.showVideoPopupYt = true;
    },
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
